import { createFileRoute } from "@tanstack/react-router";
import { Nav } from "@/components/site/Nav";
import { Hero } from "@/components/site/Hero";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloating } from "@/components/site/WhatsAppFloating";
import { Suspense, lazy, memo } from "react";

// Performance Optimization: Dynamic Imports (Code Splitting)
// These components are below the fold (LCP). Moving them to lazy loads reduces initial bundle size
// and improves TBT (Total Blocking Time) by deferring JS execution.
const Features = lazy(() => import("@/components/site/Features").then(m => ({ default: m.Features })));
const HowItWorks = lazy(() => import("@/components/site/HowItWorks").then(m => ({ default: m.HowItWorks })));
const SocialProof = lazy(() => import("@/components/site/SocialProof").then(m => ({ default: m.SocialProof })));
const FAQ = lazy(() => import("@/components/site/FAQ").then(m => ({ default: m.FAQ })));
const DeviceCompatibility = lazy(() => import("@/components/site/DeviceCompatibility").then(m => ({ default: m.DeviceCompatibility })));
const Plans = lazy(() => import("@/components/site/Plans").then(m => ({ default: m.Plans })));
const ConsultantCTA = lazy(() => import("@/components/site/ConsultantCTA").then(m => ({ default: m.ConsultantCTA })));
const PartnerApps = lazy(() => import("@/components/site/PartnerApps").then(m => ({ default: m.PartnerApps })));


// Loading skeleton for smooth CLS (Cumulative Layout Shift) management during lazy loading
const SectionLoader = () => (
  <div className="w-full h-[400px] flex items-center justify-center bg-background/50 animate-pulse">
    <div className="w-8 h-8 border-2 border-electric/30 border-t-electric rounded-full animate-spin" />
  </div>
);

export const Route = createFileRoute("/")({
  head: () => {
    const baseUrl = "https://p2propaineliptv.com.br";
    const title = "Painel IPTV Pós Pago - Melhor Servidor de Revenda IPTV 2026";
    const description = "Busca o melhor Painel IPTV Pós Pago? Somos o servidor número 1 em estabilidade 4K. Revenda IPTV profissional com ativação imediata e suporte 24h.";
    const imageUrl = "https://storage.googleapis.com/gpt-engineer-file-uploads/ExE1QnBpEVdJLYdReaoBX2uUO1B2/social-images/social-1781086883309-LOGOS_(1)_-_Copia_(2).webp";

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Como funciona o Painel IPTV Pós-Pago?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No modelo pós-pago, você utiliza os créditos primeiro e paga apenas pelo que consumiu no final do ciclo ou conforme a demanda, ideal para quem busca fluxo de caixa."
          }
        },
        {
          "@type": "Question",
          "name": "Quais as vantagens de um painel pós-pago?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Maior flexibilidade financeira, controle total sobre as ativações e a possibilidade de escalar seu negócio sem um alto investimento inicial em estoque de créditos."
          }
        },
        {
          "@type": "Question",
          "name": "O servidor é estável para transmissões 4K?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sim, nossa infraestrutura é otimizada para IPTV Pós-Pago com servidores dedicados e CDN própria, garantindo estabilidade absoluta mesmo em eventos de grande audiência."
          }
        }
      ]
    };

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "P2 PRO",
      "url": baseUrl,
      "logo": "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+55-83-98614-7064",
        "contactType": "customer service",
        "areaServed": "BR",
        "availableLanguage": "Portuguese"
      }
    };

    const webSiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "P2 PRO",
      "url": baseUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${baseUrl}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };

    return {
      meta: [
        { title: title },
        { name: "description", content: description },
        { name: "keywords", content: "painel iptv pos pago, revenda iptv profissional, melhor servidor iptv 2026, iptv pos pago oficial, painel de revenda iptv estável, servidor iptv 4k" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: imageUrl },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageUrl },
        { name: "theme-color", content: "#0a0c10" },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": title,
            "primaryImageOfPage": {
              "@type": "ImageObject",
              "url": "https://p2propaineliptv.com.br/__l5e/assets-v1/7c25e136-c4b8-4ab8-b5c7-52ef6b9b87a3/p2pro-logo.png"
            }
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqSchema),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(organizationSchema),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(webSiteSchema),
        },
      ],
    };
  },
  component: Index,
});

const MainContent = memo(() => (
  <main className="relative">
    {/* Critical Path: Rendered immediately */}
    <Hero />
    
    {/* Non-critical Path: Deferred via Suspense */}
    <Suspense fallback={<SectionLoader />}>
      <Features />
      <DeviceCompatibility />
      <PartnerApps />
      <HowItWorks />
      <Plans />
      <SocialProof />
      <FAQ />
      <ConsultantCTA />
      
    </Suspense>
  </main>
));

MainContent.displayName = "MainContent";

function Index() {
  return (
    <div className="min-h-screen bg-[#0a0c10] text-foreground selection:bg-electric/30 selection:text-white">
      {/* Structural layout with fixed/sticky Nav optimization */}
      <Nav />
      <MainContent />
      <Footer />
      <WhatsAppFloating />
    </div>
  );
}
